import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

import {
  getGetScenarioQueryKey,
  getGetScenariosQueryKey,
  useUpdateScenario,
} from '@/api/generated';
import { type ScenarioListResponse } from '@/api/generated.schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { Scenario } from '@/routes/(app)/(scenarios)/types';
import {
  replaceScenarioInListResponse,
  SCENARIO_NAME_MAX_LENGTH,
} from '@/routes/(app)/(scenarios)/utils/scenarios-list.utils';

interface ScenarioNameCellProps {
  scenario: Scenario;
}

export const ScenarioNameCell: FC<ScenarioNameCellProps> = ({ scenario }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: updateScenario, isPending } = useUpdateScenario();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Set by Escape so the blur that follows discards the edit.
  const isCancelling = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const name = scenario.name ?? '';
  // Show the typed name while the save is in flight instead of flashing the old one.
  const displayName = isPending ? draft.trim() : name;

  const handleStartEditing = () => {
    setDraft(name);
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const isCancelled = isCancelling.current;
    isCancelling.current = false;
    const nextName = draft.trim();
    if (isCancelled || nextName === name) return;

    updateScenario(
      { scenarioId: scenario.id, data: { name: nextName || null } },
      {
        onSuccess: async (updated) => {
          queryClient.setQueriesData<ScenarioListResponse>(
            { queryKey: getGetScenariosQueryKey() },
            (current) => replaceScenarioInListResponse(current, updated)
          );
          queryClient.setQueryData(getGetScenarioQueryKey(updated.id), updated);
          // A renamed scenario may no longer match the active name search.
          await queryClient.invalidateQueries({
            queryKey: getGetScenariosQueryKey(),
          });
        },
        onError: () => {
          toast.error(t('general.toast.somethingWentWrong'));
        },
      }
    );
  };

  // Enter saves and Escape cancels; both leave the input through blur.
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' && event.key !== 'Escape') return;
    event.preventDefault();
    isCancelling.current = event.key === 'Escape';
    event.currentTarget.blur();
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        className='h-8'
        value={draft}
        maxLength={SCENARIO_NAME_MAX_LENGTH}
        aria-label={t('scenarios.table.name')}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    );
  }

  return (
    <div className='group flex items-center gap-1'>
      {displayName ? (
        <span
          className='max-w-64 truncate'
          title={displayName}>
          {displayName}
        </span>
      ) : (
        <span className='text-muted-foreground'>—</span>
      )}
      <Button
        variant='ghost'
        size='icon'
        className='size-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
        disabled={isPending}
        aria-label={t('scenarios.table.editName')}
        onClick={(event) => {
          event.stopPropagation();
          handleStartEditing();
        }}>
        <Pencil className='size-4' />
      </Button>
    </div>
  );
};
